import React, { useState, useEffect, useRef } from "react";
import { bulkAddStaff } from "../api/staff-calls-api";
import Papa from "papaparse";
import * as XLSX from 'xlsx';
import countries from "../utils/countryList";
import Select from "react-select";
import { 
  FaUser, FaBuilding, FaIdCard, FaMapMarkerAlt, FaPhone, FaEnvelope, 
  FaBriefcase, FaMoneyBill, FaGraduationCap, FaUsers, FaTimes, 
  FaInfoCircle, FaCheckCircle, FaExclamationTriangle, FaCalendarAlt,
  FaFileUpload, FaFileDownload, FaPlus, FaTrash, FaTable
} from "react-icons/fa";
const countryOptions = countries.map((country) => ({
  label: `${country.flag} ${country.name}`,
  value: country.name,
}));

const today = new Date();
const maxDate = today.toISOString().split('T')[0];

const initialUser = {
  firstName: "", lastName: "", email: "", gender: "", dateOfBirth: "",
  maritalStatus: "", nationality: "", nationalIdOrPassport: "",
  villageOrBlock: "", payam: "", county: "", state: "", tel: "", mobile: "",
  role: "", jobTitle: "", department: "", dateOfEmployment: "",
  employmentType: "", contractDuration: "", salaryRange: "", bankName: "",
  bankBranch: "", accountName: "", accountNumber: "", currency: "", swiftCode: "",
  institution: "", award: "", yearFrom: "", yearTo: "", keyExpertise: "",
  recentEmployer: "", workrole: "", periodOfService: "", recentGrossAnnualPaymentRange: "",
  nokName: "", nokSex: "", nokNationality: "", nokRelationship: "",
  nokVillageOrBlock: "", nokPayam: "", nokCounty: "", nokState: "",
  nokTel: "", nokMobile: "", nokEmail: "",
};

// Helper function to convert any string to camelCase
const toCamelCase = (str) => {
  return str.replace(/[^a-zA-Z0-9]+(.)?/g, (match, chr) => chr ? chr.toUpperCase() : '').replace(/^./, (str) => str.toLowerCase());
};

export default function BulkAddStaffModal({ closeForm, onUsersAdded }) {
  const modalRef = useRef(null);
  const [users, setUsers] = useState([initialUser]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Click outside to close modal
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        closeForm();
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        closeForm();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [closeForm]);

  const handleChange = (index, e) => {
    const { name, value } = e.target;
    const newUsers = [...users];
    newUsers[index] = { ...newUsers[index], [name]: value };
    setUsers(newUsers);
  };

  const handleSelectChange = (index, name, selected) => {
    const newUsers = [...users];
    newUsers[index] = { ...newUsers[index], [name]: selected?.value || "" };
    setUsers(newUsers);
  };

  const handleAddRow = () => {
    setUsers([...users, { ...initialUser }]);
  };

  const handleRemoveRow = (index) => {
    if (users.length > 1) {
      setUsers(users.filter((_, i) => i !== index));
    }
  };

  // Centralized function to process the parsed data from any file type
  const processImportedData = (data) => {
    console.log("Raw data from file:", data);

    if (!data || data.length === 0) {
      setError("The file is empty or has no data.");
      setUsers([initialUser]);
      return;
    }

    const newUsers = data.map(item => {
      let userObject = { ...initialUser };
      for (const key in item) {
        if (Object.prototype.hasOwnProperty.call(item, key)) {
          const normalizedKey = toCamelCase(key);
          if (userObject.hasOwnProperty(normalizedKey)) {
            userObject[normalizedKey] = item[key];
          }
        }
      }
      return userObject;
    });

    console.log("Processed users:", newUsers);
    
    // Filter out rows that are entirely empty
    const filteredUsers = newUsers.filter(user => user.email || user.firstName || user.lastName);
    
    if (filteredUsers.length > 0) {
      setUsers(filteredUsers);
    } else {
      setError("The file contains no valid staff data. Please check the columns.");
      setUsers([initialUser]);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    setError("");
    if (file) {
      const fileExtension = file.name.split('.').pop().toLowerCase();
      if (fileExtension === 'csv') {
        Papa.parse(file, {
          header: true,
          skipEmptyLines: true,
          complete: (results) => {
            processImportedData(results.data);
          },
          error: (error) => {
            setError("Failed to parse CSV file. Please check the format.");
            console.error("CSV parse error:", error);
          }
        });
      } else if (fileExtension === 'xlsx') {
        const reader = new FileReader();
        reader.onload = (event) => {
          try {
            const data = new Uint8Array(event.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, {
              defval: '', // Set default value for empty cells
            });
            processImportedData(jsonData);
          } catch (err) {
            setError("Failed to parse Excel file. Please ensure it's a valid XLSX file.");
            console.error("Excel parse error:", err);
          }
        };
        reader.readAsArrayBuffer(file);
      } else {
        setError("Unsupported file format. Please upload a .csv or .xlsx file.");
      }
    }
  };

  const handleDownloadTemplate = () => {
    // Generate human-readable headers from the initialUser object keys
    const headers = Object.keys(initialUser).map(key => {
      // Convert camelCase to "First Name" style
      return key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
    });
    const csvContent = headers.join(',');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", "staff_template.csv");
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    const usersToSubmit = users.filter(u => u.email && u.firstName);
    if (usersToSubmit.length === 0) {
      return setError("No valid staff members to add. Please fill in at least the email and first name for a user.");
    }

    setLoading(true);
    const token = localStorage.getItem("token");
    if (!token) return setError("You must be logged in.");

    try {
      await bulkAddStaff(usersToSubmit, token);
      setSuccess(`${usersToSubmit.length} staff members added successfully!`);
      onUsersAdded();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "An error occurred.");
    } finally {
      setLoading(false);
    }
  };

  const tableHeaders = [
    "First Name", "Last Name", "Email", "Gender", "Date of Birth", "Marital Status",
    "Role", "Nationality", "Job Title", "Employment Type", "Nok Name", "Nok Sex", "Nok Nationality"
  ];

  const renderField = (user, index, field, type = "text", options = null, icon = null) => {
    const isRequired = ["firstName", "lastName", "email", "gender", "dateOfBirth", "maritalStatus", "role", "nationality"].includes(field);
    const value = user[field];

    if (options) {
      return (
        <div className="relative">
          {icon && <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">{icon}</div>}
          <select
            name={field}
            value={value}
            onChange={(e) => handleChange(index, e)}
            className={`p-2.5 border rounded-lg w-full text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${icon ? 'pl-10' : ''}`}
            required={isRequired}
          >
            <option value="">-- Select --</option>
            {options.map((opt, i) => (
              <option key={i} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      );
    }

    if (field === "nationality" || field === "nokNationality") {
      return (
        <Select
          options={countryOptions}
          value={countryOptions.find(opt => opt.value === value)}
          onChange={(selected) => handleSelectChange(index, field, selected)}
          className="react-select-container w-full"
          classNamePrefix="react-select"
          placeholder="Select..."
          isClearable
          styles={{
            control: (base, state) => ({
              ...base,
              borderRadius: '0.5rem',
              minHeight: '40px',
              borderColor: state.isFocused ? '#F97316' : '#D1D5DB',
              boxShadow: state.isFocused ? '0 0 0 1px #F97316' : 'none',
              '&:hover': {
                borderColor: '#F97316'
              }
            }),
            placeholder: (base) => ({
              ...base,
              color: '#9CA3AF'
            }),
            singleValue: (base) => ({
              ...base,
              color: '#1F2937'
            }),
            option: (base, state) => ({
              ...base,
              backgroundColor: state.isSelected ? '#F97316' : state.isFocused ? '#FFEDD5' : 'white',
              color: state.isSelected ? 'white' : '#1F2937',
              '&:hover': {
                backgroundColor: '#FFEDD5',
                color: '#1F2937'
              }
            }),
            dropdownIndicator: (base) => ({
              ...base,
              color: '#6B7280',
              '&:hover': {
                color: '#F97316'
              }
            }),
            indicatorSeparator: (base) => ({
              ...base,
              backgroundColor: '#D1D5DB'
            }),
            menu: (base) => ({
              ...base,
              borderRadius: '0.5rem',
              border: '1px solid #E5E7EB'
            })
          }}
        />
      );
    }
    
    if (type === "date") {
      return (
        <div className="relative">
          {icon && <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">{icon}</div>}
          <input
            type="date"
            name={field}
            value={value}
            onChange={(e) => handleChange(index, e)}
            className={`p-2.5 border rounded-lg w-full text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${icon ? 'pl-10' : ''}`}
            required={isRequired}
            max={maxDate}
          />
        </div>
      );
    }

    return (
      <div className="relative">
        {icon && <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">{icon}</div>}
        <input
          type={type}
          name={field}
          value={value}
          onChange={(e) => handleChange(index, e)}
          className={`p-2.5 border rounded-lg w-full text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${icon ? 'pl-10' : ''}`}
          required={isRequired}
        />
      </div>
    );
  };

  const renderTableRow = (user, index) => (
    <tr key={index} className="border-b hover:bg-gray-50">
      <td className="p-2">{renderField(user, index, "firstName", "text", null, <FaUser className="text-gray-400" />)}</td>
      <td className="p-2">{renderField(user, index, "lastName", "text", null, <FaUser className="text-gray-400" />)}</td>
      <td className="p-2">{renderField(user, index, "email", "email", null, <FaEnvelope className="text-gray-400" />)}</td>
      <td className="p-2">{renderField(user, index, "gender", "select", ["Male", "Female"], <FaUser className="text-gray-400" />)}</td>
      <td className="p-2">{renderField(user, index, "dateOfBirth", "date", null, <FaCalendarAlt className="text-gray-400" />)}</td>
      <td className="p-2">{renderField(user, index, "maritalStatus", "select", ["Single", "Married", "Other"], <FaUser className="text-gray-400" />)}</td>
      <td className="p-2">{renderField(user, index, "role", "select", ["admin", "hr_officer", "directors", "head_of_operations", "finance_officer", "operations_associate", "project_officer", "field_officer"], <FaBriefcase className="text-gray-400" />)}</td>
      <td className="p-2">{renderField(user, index, "nationality", "select", null, <FaMapMarkerAlt className="text-gray-400" />)}</td>
      <td className="p-2">{renderField(user, index, "jobTitle", "text", null, <FaBriefcase className="text-gray-400" />)}</td>
      <td className="p-2">{renderField(user, index, "employmentType", "select", ["Full-time", "Part-time", "Contract", "Internship"], <FaBriefcase className="text-gray-400" />)}</td>
      <td className="p-2">{renderField(user, index, "nokName", "text", null, <FaUsers className="text-gray-400" />)}</td>
      <td className="p-2">{renderField(user, index, "nokSex", "select", ["Male", "Female", "Other"], <FaUser className="text-gray-400" />)}</td>
      <td className="p-2">{renderField(user, index, "nokNationality", "select", null, <FaMapMarkerAlt className="text-gray-400" />)}</td>
      <td className="p-2">
        <button type="button" onClick={() => handleRemoveRow(index)} className="text-red-500 hover:text-red-700 font-bold p-2" disabled={users.length <= 1}>
          <FaTrash />
        </button>
      </td>
    </tr>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      {/* Popup Notifications */}
      {error && (
        <div className="fixed top-5 left-1/2 transform -translate-x-1/2 z-50">
          <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-xl shadow-lg flex items-center space-x-3 animate-fade-in-out">
            <FaExclamationTriangle className="text-red-500 flex-shrink-0" />
            <span>{error}</span>
          </div>
        </div>
      )}
      {success && (
        <div className="fixed top-5 left-1/2 transform -translate-x-1/2 z-50">
          <div className="bg-green-50 border border-green-200 text-green-700 px-6 py-4 rounded-xl shadow-lg flex items-center space-x-3 animate-fade-in-out">
            <FaCheckCircle className="text-green-500 flex-shrink-0" />
            <span>{success}</span>
          </div>
        </div>
      )}

      <div
        ref={modalRef}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[95vh] flex flex-col overflow-hidden transform transition-all duration-300 ease-out"
      >
        {/* Header with Orange Gradient */}
        <div className="flex-shrink-0 bg-gradient-to-r from-orange-200 to-orange-200 border-b border-orange-50">
          <div className="flex items-center justify-between px-8 py-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl shadow-sm">
                <FaTable className="w-7 h-7 text-black" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-black">Bulk Add Staff</h2>
                <p className="text-black-100 mt-1">Staff Management System</p>
              </div>
            </div>
            <button
              onClick={closeForm}
              className="p-2 rounded-xl transition-all duration-200 text-orange-200 hover:bg-white/20 hover:text-white hover:shadow-sm"
            >
              <FaTimes className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-grow px-8 py-6 overflow-y-auto">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* File Upload Section */}
            <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <FaFileUpload className="text-orange-500" /> Import Staff Data
              </h3>
              
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex-grow">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Upload CSV or Excel File (.xlsx):</label>
                  <div className="relative">
                    <input 
                      type="file" 
                      accept=".csv, .xlsx" 
                      onChange={handleFileUpload} 
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" 
                    />
                    <div className="flex items-center justify-center w-full h-12 px-4 transition-all duration-200 border-2 border-dashed border-gray-300 rounded-lg bg-white hover:border-orange-500 hover:bg-orange-50">
                      <FaFileUpload className="text-orange-500 mr-2" />
                      <span className="text-gray-700">Choose file or drag here</span>
                    </div>
                  </div>
                </div>
                <div>
                  <button
                    type="button"
                    onClick={handleDownloadTemplate}
                    className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-orange-700 bg-orange-100 rounded-lg hover:bg-orange-200 transition-colors"
                  >
                    <FaFileDownload />
                    Download Template
                  </button>
                </div>
              </div>
            </div>

            {/* Data Table Section */}
            <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <FaTable className="text-orange-500" /> Staff Data
                </h3>
                <button
                  type="button"
                  onClick={handleAddRow}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                >
                  <FaPlus /> Add Row
                </button>
              </div>
              
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-100">
                    <tr>
                      {tableHeaders.map((header, i) => (
                        <th key={i} className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                          {header}
                          {["First Name", "Last Name", "Email", "Gender", "Date of Birth", "Marital Status", "Role", "Nationality"].includes(header) && 
                            <span className="text-red-500 ml-1">*</span>}
                        </th>
                      ))}
                      <th className="p-3"></th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {users.map((user, index) => renderTableRow(user, index))}
                  </tbody>
                </table>
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 bg-gray-50 px-8 py-6 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <FaInfoCircle className="w-4 h-4 text-orange-500" />
                <span>Fields marked with * are required</span>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <button
                type="button"
                onClick={closeForm}
                className="px-6 py-3 rounded-xl font-semibold transition-all duration-200 text-gray-700 hover:bg-gray-200"
                disabled={loading}
              >
                Cancel
              </button>

              <button
                type="submit"
                form="bulk-staff-form"
                className={`relative px-8 py-3 rounded-xl font-semibold text-white transition-all duration-200 transform ${
                  loading
                    ? 'bg-orange-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 hover:scale-105 shadow-lg hover:shadow-xl'
                }`}
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Processing...</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <FaTable />
                    <span>Save All Staff</span>
                  </div>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}